/**
 * vet-visit-edit.js
 */

import React from 'react';
import axios from 'axios';
import {
  IconButton,
  Alert,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  useHistory,
} from "react-router-dom";
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import { none } from '@hookstate/core'
import { useSnackbar } from 'notistack';
import { parseISO, parse, formatISO, addYears, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { sleep } from "utils/misc";
import useMounted from 'utils/use-mounted';
import { dateParamFormat } from 'utils/datetime';
import { DogsContext } from 'contexts/dogs/dogs-context';
import useRecordStore from 'contexts/record-store';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerContent from 'components/menu-drawer/drawer-content';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import FormGridContainer from 'components/menu-drawer/form-grid-container';
import FormGridItem from 'components/menu-drawer/form-grid-item';
import Icon from 'components/icon';
import TextField from 'components/form-inputs/text-field';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import Autocomplete from 'components/form-inputs/autocomplete';
import FilesEdit from 'components/files/edit';
import DateTimePicker from 'components/form-inputs/date-time-picker';
import Select from 'components/form-inputs/select';
import CheckboxList from 'components/form-inputs/checkbox-list';
import VetVisitEditVaccinations from './vet-visit-edit-vaccinations';
import TitleEditor from 'components/title-editor';
import InlineHelpLink from 'components/inline-help-link';


// use wrapper to remount component when visit ID changes
export default function VetVisitEdit(props) {
  const vetVisitId = props.params?.id;

  return <VetVisitEditImplementation
    vetVisitId={vetVisitId}
    key={vetVisitId}
    {...props}
  />;
}


function VetVisitEditImplementation(props) {
  const {
    vetVisitId,
    drawerState,
    onClose,
    wizard,
    basePath,
  } = props;
  const translation = 'vetVisitEdit';
  const { t } = useTranslation(translation);
  const isMounted = useMounted();
  let history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { getActiveDogs, fetchDogs } = React.useContext(DogsContext);
  const recordStore = useRecordStore();
  const vetVisitRecord = recordStore.useRecord(Types.Record.VET_VISIT, vetVisitId);
  const [storedRecord, setStoredRecord] = React.useState();
  const [options, setOptions] = React.useState();
  const [submittedState, setSubmittedState] = React.useState();
  const isMobileDrawer = useMediaQuery(theme => theme.breakpoints.down('sm_md'), { noSsr: true });
  const dogs = React.useMemo(() => getActiveDogs(), [getActiveDogs]);
  

  // hook form
  const formContext = useForm();
  const { control, formState, handleSubmit, reset, setValue } = formContext;
  const dirtyFields = formState.dirtyFields;
  const nameValue = useWatch({ control, name: 'name' });
  const dateValue = useWatch({ control, name: 'date' });
  const locationNameValue = useWatch({ control, name: 'location' });


  const isNameCustomized = React.useMemo(() => {
    const autoNameRegex = /^Vet Visit - (January|February|March|April|May|June|July|August|September|October|November|December) \d{4}$/i;
    return !autoNameRegex.test(nameValue);
  }, [nameValue]);


  // get visit options on init
  React.useEffect(() => {
    if (!Number.isInteger(vetVisitId)) {
      return;
    }

    setTimeout(() => drawerState.loading.content.set(true));
    (async () => {
      try {
        const dateParam = new URLSearchParams(window.location.search).get('date');
        const date = formatISO(dateParam
          ? parse(dateParam, dateParamFormat, new Date())
          : new Date());
        const response = await axios.get(`/vet/${vetVisitId}/edit`, { params: { date } });

        if (!isMounted.current) return;
        if (response.status !== 200) return;

        // this gives processing time for the drawer to animate open before drawing the form
        await sleep(300);

        const recordData = response.data;
        const vetVisit = JSON.parse(JSON.stringify(recordStore.updateRecord(recordData.vetVisit).get()));
        reset(vetVisit);
        setStoredRecord(vetVisit);
        setOptions(recordData.options);

        drawerState.loading.content.set(false);
      } catch (e) {
        if (e.response && e.response.status === 404) {
          return history.push(basePath);
        }
        if (e.response && e.response.status === 403) {
          return history.push(makeLocation(
            basePath,
            'vetVisit',
            'view',
            { vetVisitId },
            history.location
          ));
        }
      }
    })();

    // reset 0 state on closing so we don't have bad cached data later
    return () => {
      if (vetVisitId === 0) {
        setTimeout(() => vetVisitRecord.set(none), 1000);
      }
    }

    // recordStore & drawerState & eventRecord are hookstates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vetVisitId, basePath, history, isMounted, reset]);


  // when date or location name changes, update eventRecord for menu consistency
  React.useEffect(() => {
    if (nameValue === undefined
      || !dateValue || dateValue.toString() === 'Invalid Date'
      || locationNameValue === undefined
    ) {
      return;
    }

    vetVisitRecord.set(p => ({
      ...p,
      name: nameValue,
      location: locationNameValue,
      date: typeof dateValue === 'string'
        ? formatISO(parseISO(dateValue))
        : formatISO(dateValue),
    }));

    // vetVisitRecord is setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue, nameValue, locationNameValue]);


  // when date/time changes -> update name (unless its been customized already)
  React.useEffect(() => {
    if (!dateValue
      || typeof dateValue === 'string'
      || (dateValue && dateValue.toString() === 'Invalid Date')
      || isNameCustomized
    ) {
      return;
    }

    setValue('name', `Vet Visit - ${format(dateValue, 'MMMM yyyy')}`);
  }, [dateValue, nameValue, isNameCustomized, setValue]);


  // Ensure that ConfirmUnsavedEditor props are up to date by triggering 
  // a re-render before proceding with save and navigation.
  // (onSubmit > useEffect > afterSubmit)
  const onSubmit = React.useCallback((data, noNav) => {
    setSubmittedState({ data, noNav });
  }, []);


  const afterSubmit = React.useCallback(async (data, noNav) => {
    drawerState.loading.content.set(true);

    try {
      const prevVetVisitDate = storedRecord.id === 0 ? null : storedRecord.date;
      const { record: savedVetVisitRecord } = await recordStore.saveVetVisit(vetVisitId, data, prevVetVisitDate);

      // Update dogs context data after possible vaccination change
      fetchDogs();

      if (savedVetVisitRecord.get()) {

        // regular class save
        enqueueSnackbar('Vet visit saved', {
          variant: 'success'
        });

        if (noNav !== true) {
          history.push(makeLocation(
            basePath,
            'vetVisit',
            'view',
            { vetVisitId: savedVetVisitRecord.get()?.id },
            history.location
          ));
        }
      }
    } catch (e) {
      enqueueSnackbar('Error saving vet visit', {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });

      drawerState.loading.content.set(false);
      throw e;
    }

    // recordStore & drawerState & exerciseRecord are hookstates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vetVisitId, basePath, enqueueSnackbar, history, wizard, dirtyFields, isMounted, storedRecord]);


  React.useEffect(() => {
    if (submittedState) {
      afterSubmit(submittedState.data, submittedState.noNav);
    }
    // only call afterSubmit when submittedState changes, not when afterSubmit itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedState]);


  const onSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);


  const onCancel = React.useCallback(() => {
    if (vetVisitId === 0) {
      history.goBack();
    } else {
      // go back to visit view
      history.push(makeLocation(
        basePath,
        'vetVisit',
        'view',
        { vetVisitId },
        history.location
      ));
    }
  }, [history, basePath, vetVisitId]);


  const onDiscardChanges = React.useCallback(() => {
    vetVisitRecord.set(storedRecord);
  }, [vetVisitRecord, storedRecord]);


  const saveButton = React.useMemo(() => (
    <DrawerActionButton
      level={1}
      disabled={!formState.isDirty && !wizard}
      style={{ marginLeft: 16 }}
      onClick={onSave}
      data-testid='vet-visit-save-button'
    >
      {wizard ? 'Next' : 'Save'}
    </DrawerActionButton>
  ), [wizard, onSave, formState.isDirty]);


  const cancelButton = React.useMemo(() => (
    <DrawerActionButton
      onClick={onCancel}
    >
      Cancel
    </DrawerActionButton>
  ), [onCancel]);


  const closeButton = React.useMemo(() => (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{ color: 'inherit' }}
      onClick={onClose}
    >
      <Icon type='close' />
    </IconButton>
  ), [onClose]);

  const desktopTitle = React.useMemo(() => (
    <>
      {storedRecord &&
        <TitleEditor
          propertyName='name'
          inputLabel={t('main.name.label')}
          helperText={!isNameCustomized ? t('main.name.extraInfo') : ''}
          validationRules={{
            required: t('main.name.errMsgEmpty'),
            maxLength: {
              value: 250,
              message: t('main.name.errMsgLength')
            }
          }}
        />}
    </>
  ), [storedRecord, isNameCustomized, t]);

  return (
    <>
      <FormProvider {...formContext}>
        <DrawerContentBuilder
          mobileAppBar={{
            title: 'Vet Visit',
            leftActions: vetVisitId === 0 ? closeButton : cancelButton,
            rightActions: saveButton,
            recordGroupType: Types.RecordGroup.VET_VISIT,
          }}
          desktopHeader={{
            title: desktopTitle,
            actions: <>{cancelButton}{saveButton}</>
          }}
          stickyHeader
          children={(triggerElement, desktopHeader) => <>
            {(storedRecord && options) &&
              <form onSubmit={handleSubmit(onSubmit)}>
                {desktopHeader}
                <MobilePageTitle title={vetVisitRecord.get()?.name} />
                <DrawerContent>

                  {triggerElement}

                  <Typography variant='body1' mb={5}>
                    Record each veterinary visit to stay on top of your K9's health.
                    <InlineHelpLink intercomId={10930639} />
                  </Typography>

                  <EditorIconSection
                    iconName='summary'
                    helpProps={{
                      translation,
                      section: 'main'
                    }}
                  >

                    {/* vet visit name */}
                    {isMobileDrawer &&
                      <HelpTextItem
                        sidebarText={drawerState.sidebarText}
                        title={t('main.name.helpTitle')}
                        text={t('main.name.helpInfo')}
                      >
                        {focusHandlers =>
                          <FormGridContainer>
                            <FormGridItem md={8} lg={8} xl={6}>
                              <Autocomplete
                                name='name'
                                label={t('main.name.label')}
                                options={options.names}
                                focusHandlers={focusHandlers}
                                rules={{
                                  required: t('main.name.errMsgEmpty'),
                                  maxLength: {
                                    value: 250,
                                    message: t('main.name.errMsgLength')
                                  }
                                }}
                              />
                            </FormGridItem>
                          </FormGridContainer>}
                      </HelpTextItem>
                    }

                    {dogs.length === 0 &&
                      <Alert severity='warning'>
                        <Typography>
                          You don't have any active dogs, so you won't be able to complete the form.
                        </Typography>
                      </Alert>
                    }

                    {/* dog */}
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('main.dog.helpTitle')}
                      text={t('main.dog.helpInfo')}
                    >
                      {focusHandlers =>
                        <FormGridContainer>
                          <FormGridItem>
                            <Select
                              name='dog'
                              label={t('main.dog.label')}
                              focusHandlers={focusHandlers}
                              defaultValue=''
                              options={dogs}
                              setValueAs={id => ({ id })}
                              rules={{ required: t('main.dog.errMsgEmpty') }}
                            />
                          </FormGridItem>
                        </FormGridContainer>}
                    </HelpTextItem>
                  </EditorIconSection>

                  {/* location */}
                  <EditorIconSection
                    iconName='location'
                    helpProps={{
                      translation,
                      section: 'location'
                    }}
                  >
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('location.location.helpTitle')}
                      text={t('location.location.helpInfo')}
                    >
                      {focusHandlers =>
                        <FormGridContainer>
                          <FormGridItem md={8} lg={8} xl={6}>
                            <Autocomplete
                              name='location'
                              label={t('location.location.label')}
                              options={options.locations}
                              focusHandlers={focusHandlers}
                              rules={{
                                maxLength: {
                                  value: 250,
                                  message: t('location.location.errMsgLength')
                                }
                              }}
                            />
                          </FormGridItem>
                        </FormGridContainer>}
                    </HelpTextItem>
                  </EditorIconSection>

                  {/* start date/time */}
                  <EditorIconSection
                    iconName='event'
                    helpProps={{
                      translation,
                      section: 'dateTime'
                    }}
                  >
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('dateTime.dateTime.helpTitle')}
                      text={t('dateTime.dateTime.helpInfo')}
                    >
                      {focusHandlers =>
                        <FormGridContainer>
                          <FormGridItem md={8} lg={8} xl={6}>
                            <DateTimePicker
                              variant='datetime'
                              name='date'
                              label={t('dateTime.dateTime.label')}
                              focusHandlers={focusHandlers}
                              format="MM/dd/yyyy HH:mm"
                              showTodayButton={true}
                              rules={{
                                required: t('dateTime.dateTime.errMsgEmpty'),
                              }}
                              maxDate={addYears(new Date(), 10)}
                              maxDateMessage={t('dateTime.dateTime.errMsgMaxDate')}
                              minDate={addYears(new Date(), -10)}
                              minDateMessage={t('dateTime.dateTime.errMsgMinDate')}
                            />
                          </FormGridItem>
                        </FormGridContainer>
                      }

                    </HelpTextItem>
                  </EditorIconSection>

                  {/* care type */}
                  <EditorIconSection
                    iconName='vet-care-type'
                    helpProps={{
                      translation,
                      section: 'careType'
                    }}
                  >
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('careType.careType.helpTitle')}
                      text={t('careType.careType.helpInfo')}
                    >
                      {focusHandlers =>
                        <CheckboxList
                          name='careTypes'
                          label={t('careType.careType.label')}
                          helperText={t('careType.careType.helpInfo')}
                          options={options.vetCareTypes}
                          focusHandlers={focusHandlers}
                          customOther
                          rules={{
                            validate: val => (val && val.length > 0) || t('careType.careType.errMsgEmpty')
                          }}
                        />}
                    </HelpTextItem>
                  </EditorIconSection>

                  <VetVisitEditVaccinations
                    vaccinationCoreTypes={options.vaccinationCoreTypes}
                    vaccinationNonCoreTypes={options.vaccinationNonCoreTypes}
                    drawerState={drawerState}
                    translation={translation}
                  />

                  {/* notes */}
                  <EditorIconSection
                    iconName='comment'
                    helpProps={{
                      translation,
                      section: 'notes'
                    }}
                  >
                    <HelpTextItem
                      sidebarText={drawerState.sidebarText}
                      title={t('notes.notes.helpTitle')}
                      text={t('notes.notes.helpInfo')}
                    >
                      {focusHandlers =>
                        <FormGridContainer>
                          <FormGridItem sm={12} md={12} lg={12} xl={12}>
                            <TextField
                              name='notes'
                              label={t('notes.notes.label')}
                              focusHandlers={focusHandlers}
                              style={{ width: '100%' }}
                              multiline
                              rows={12}
                              rules={{
                                // notes are optional but if the record has notes then they can't be removed
                                required: vetVisitRecord.get()?.notes && t('notes.notes.errMsgEmpty'),
                              }}
                            />
                          </FormGridItem>
                        </FormGridContainer>}
                    </HelpTextItem>
                  </EditorIconSection>

                  {/* files */}
                  <FilesEdit
                    name='files'
                    drawerState={drawerState}
                    translation={translation}
                  />

                </DrawerContent>

              </form>
            }
          </>}
        />
      </FormProvider>


      <ConfirmUnsavedEditor
        dirty={formState.isDirty && !formState.isSubmitted}
        discard={onDiscardChanges}
        save={async () => {
          return await new Promise((resolve) => {
            handleSubmit(
              async (data) => {
                onSubmit(data, true);
                return resolve(true);
              },
              () => {
                return resolve(false);
              })();
          });
        }}
      />

    </>
  );
}